import React, { FC, useContext, ReactNode } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';
import styled, { css } from 'styled-components';

import AddInStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/add-stroke';
import FormContext from 'src/presentation/contexts/FormContext';
import RemoveFillIcon from '@ad-scripts/presentation/components/common/icons/fill/remove-fill';
import { Button, Collapsed } from 'src/presentation/components/agent_desktop_components';
import { ErrorLabel } from '@ad-scripts/presentation/components/common/forms/control_container';
import { H4 } from '@ad-scripts/presentation/components/common/headers';
import { StyledContent, StyledHeader, StyledIcon } from '@ad-scripts/presentation/components/common/collapse';

type EditorSectionProps = {
  'data-aid': string;
  addButtonLabel: string;
  attr: string;
  className?: string;
  content?: ReactNode;
  description?: string;
  errors: any[];
  footer?: JSX.Element;
  getItemKey?: (item) => string;
  getNewItemDefaultData: Function;
  hideAddButton?: boolean;
  hideOptions?: boolean;
  maxItems?: number;
  minItems?: number;
  onDelete?: (id: string, index: number) => void;
  prefixWord?: ({ index }) => string;
  renderItem: ({ id, index }) => any;
  sectionHeader?: JSX.Element;
  tableHeader?: JSX.Element;
  title?: string;
};

export const EditorSection: FC<EditorSectionProps> = function ({
  'data-aid': dataAid,
  addButtonLabel,
  attr,
  className,
  content,
  description,
  errors,
  footer,
  getItemKey,
  getNewItemDefaultData,
  hideAddButton,
  hideOptions,
  maxItems,
  minItems,
  onDelete,
  prefixWord,
  renderItem,
  sectionHeader,
  tableHeader,
  title,
}) {
  const { workingState, updateState } = useContext(FormContext);
  const items = get(workingState, attr, []);

  const canDelete = !minItems || items.length > minItems;
  const canAdd = !hideAddButton && (!maxItems || items.length < maxItems);

  const onAddButtonClick = () => {
    const cloneItems: any[] = cloneDeep(items);
    cloneItems.push(getNewItemDefaultData());
    updateState(set({}, attr, cloneItems));
  };
  const error = find(errors, error => get(error, 'attr', '').startsWith(attr));
  const onDeleteButtonClick = ({ id, index }) => {
    if (onDelete) {
      onDelete(id, index);
      return;
    }

    updateState(
      set(
        {},
        attr,
        filter(cloneDeep(items), (item: any) => item.id !== id)
      ),
      { replaceArrayValues: true }
    );
  };

  const generateItemKey = getItemKey ? getItemKey : item => item.id;

  const itemsRender = map(items, (item: any, index: number) => {
    const prefix = prefixWord ? prefixWord({ index }) : '';

    return (
      <RowContainer key={generateItemKey(item) || index}>
        {prefixWord && <PrefixContainer textIsEmpty={!prefix}>{prefix}</PrefixContainer>}
        <ConditionContainer>{renderItem({ id: item.id, index })}</ConditionContainer>
        <ItemActions>
          {canDelete && (
            <StyledTrashIcon
              data-aid={`item-row[${index}]-deleteButton`}
              width={16}
              onClick={() => onDeleteButtonClick({ id: item.id, index })}
            />
          )}
        </ItemActions>
      </RowContainer>
    );
  });

  const itemsLength = items?.length;

  const optionTable = (
    <OptionTable>
      <tbody>
        {tableHeader && (
          <tr>
            {prefixWord && <PrefixContainer textIsEmpty={true}></PrefixContainer>}
            <ConditionContainer>{tableHeader}</ConditionContainer>
            <ItemActions></ItemActions>
          </tr>
        )}
        {itemsLength ? itemsRender : null}
      </tbody>
    </OptionTable>
  );

  return (
    <div className={className} data-aid={dataAid}>
      <OptionSection
        actionRender={
          <StyledSectionContainer>
            {canAdd && <AddButton text={addButtonLabel} onClick={onAddButtonClick} />}
          </StyledSectionContainer>
        }
        data-aid={`${dataAid}-optionsContainer`}
        isError={!!error}
        title={title}
        description={description}
        content={content}
        sectionHeader={sectionHeader}
        footer={footer}
        hideOptions={hideOptions}
      >
        {optionTable}
      </OptionSection>
      {error && (
        <ErrorContainer>
          <StyleErrorLabel data-aid="error">{get(error, 'detail')}</StyleErrorLabel>
        </ErrorContainer>
      )}
    </div>
  );
};

type OptionSection = {
  'data-aid': string;
  actionRender?: ReactNode;
  content?: ReactNode;
  description?: string;
  footer?: JSX.Element;
  hideOptions?: boolean;
  isError: boolean;
  sectionHeader?: JSX.Element;
  title?: string;
};

export const OptionSection: FC<OptionSection> = ({
  'data-aid': dataAid,
  actionRender,
  children,
  content,
  description,
  footer,
  hideOptions,
  isError,
  sectionHeader,
  title,
}) => {
  return (
    <StyledOptionsSection
      className="collapsible-section"
      data-aid={dataAid}
      footer={footer}
      header={sectionHeader}
      isActive={true}
      isError={isError}
    >
      {content}
      {!hideOptions && (
        <>
          {title && (
            <StyledSectionContainer>
              <SectionTitle data-aid="title">{title}</SectionTitle>
            </StyledSectionContainer>
          )}
          {description && <DescriptionSection data-aid={dataAid + '-description'}>{description}</DescriptionSection>}
          {children}
          {actionRender}
        </>
      )}
    </StyledOptionsSection>
  );
};

type AddButtonProps = {
  text: string;
  onClick?: Function;
};

export const AddButton: FC<AddButtonProps> = function ({ text, onClick }) {
  return (
    <StyledAddButton type={Button.Types.TEXT} data-aid="addButton" onClick={onClick}>
      <AddInStrokeIcon /> {text}
    </StyledAddButton>
  );
};

export const OptionTable = styled.table`
  margin: 4px 0 4px 0;
  border-spacing: 0px;
  width: 100%;
`;

export const ErrorContainer = styled.div`
  margin-bottom: 8px;
`;

const StyleErrorLabel = styled(ErrorLabel)`
  &:first-letter {
    text-transform: uppercase;
  }
`;

export const SectionTitle = styled(H4)`
  margin-bottom: 0px;
`;

const RowContainer = styled.tr`
  &:first-child td:first-child {
    border-top-left-radius: 10px;
  }
  &:first-child td:last-child {
    border-top-right-radius: 10px;
  }

  &:last-child td:first-child {
    border-bottom-left-radius: 10px;
  }
  &:last-child td:last-child {
    border-bottom-right-radius: 10px;
  }

  &:hover {
    background: ${p => p.theme.colors.green100};
    svg {
      visibility: visible;
    }
  }
`;

const EmptyPrefix = css`
  padding: 0;
  width: 8px;
`;

const NotEmptyPrefix = css`
  width: 36px;
  padding: 18px 8px 0 16px;
  vertical-align: baseline;
`;

export const PrefixContainer = styled.td`
  ${p => (p.textIsEmpty ? EmptyPrefix : NotEmptyPrefix)}
`;

export const ConditionContainer = styled.td`
  padding: 8px;
`;

export const ItemActions = styled.td`
  width: 24px;
`;

const StyledTrashIcon = styled(RemoveFillIcon)`
  fill: ${p => p.theme.colors.gray600};
  cursor: pointer;
  visibility: hidden;
`;

export const StyledSectionContainer = styled.div`
  line-height: 162%;
  padding: 0 16px;
`;

export const InputFieldContainer = styled.div`
  margin-left: 8px;
  input {
    max-width: 60px;
  }
`;

const StyledAddButton = styled(Button)`
  color: ${p => p.theme.colors.green400};
  padding: 0;
  display: flex;
  align-items: center;

  svg {
    fill: ${p => p.theme.colors.green400};
    margin-right: 4px;
    width: 12px;
  }

  &:hover {
    color: ${p => p.theme.colors.green500};
  }
`;

export const StyledOptionsSection = styled(Collapsed)`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  padding: ${p => p.theme.spacing.stackMedium} 0;
  margin-bottom: ${p => (p.isError ? '4px' : '16px')};

  ${StyledIcon} {
    top: 12px;
  }

  ${StyledContent} {
    padding: 0;
  }

  ${StyledHeader} {
    padding: 0;
  }
`;

export const DescriptionSection = styled.div`
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
`;
